import React, { useState, useEffect, useCallback } from "react";
import {
    Typography, Link, Card, CardContent, CardHeader, Avatar, Grid, Stack,
    Collapse, Button, TextField
} from '@mui/material';
import PhoneIcon from '@mui/icons-material/Phone';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { GetOrg, GetResolvedPricing } from '../../../../api'

const Home = ({ user, access }) => {

    const [orgs, setOrgs] = useState([])
    const [expandedOrg, setExpandedOrg] = useState(null)
    const [pricingData, setPricingData] = useState({})
    const [billingFilter, setBillingFilter] = useState('')

    useEffect(() => {
        setOrgs([])
        setExpandedOrg(null)
        setPricingData({})
        GetOrg(access.org.id).then(resp => {
            const { orgs } = resp
            if (!orgs?.length) return
            setOrgs(orgs)
            // Fetch pricing for all orgs to know which have it
            orgs.forEach(org => {
                Promise.all([
            GetResolvedPricing(org.id, access.org.id).catch(() => ({ billing: [], priorities: [] }))
        ]).then(([data]) => {
            const hasPricing = data.billing?.some(b => b.price) || data.priorities?.some(p => p.weekdayPrice || p.weekendPrice)
            if (hasPricing)
                setPricingData(prev => ({ ...prev, [org.id]: data }))
        })
            })
        }).catch(e => e)
    }, [access])

    const togglePricing = useCallback(org => {
        setExpandedOrg(prev => prev?.id === org.id ? null : org)
    }, [])

    const expanded = expandedOrg && pricingData[expandedOrg.id]

    return (
        <>
            <Typography variant='h3'>Hello {user.name}!</Typography>
            {(access?.org.id === 0) && (<Typography sx={{ mt: 3 }} color="secondary">This account hasn't yet been assigned permissions. Please contact your telespeciality provider to enable access.</Typography>)}
            {!!orgs.length && (<>
                <Typography variant='h5' sx={{ mt: 3 }}>Telespecialty groups:</Typography>
                <Grid container spacing={3}>
                    {orgs.map(org => (
                        <Grid xs={12} sm={6} md={4} key={org.id}>
                            <Card sx={{ minHeight: 220, p: 2 }}>
                                <CardHeader
                                    avatar={
                                        <Avatar
                                            src={org.logo}
                                            alt={org.name}
                                            sx={{ width: 56, height: 56, bgcolor: 'white', img: { objectFit: 'contain' } }}
                                        />
                                    }
                                    title={<Typography variant="h6">{org.name}</Typography>}
                                    subheader={
                                        (Array.isArray(org.email) ? org.email : [org.email]).filter(Boolean).map((email, i) => (
                                            <Link key={i} href={`mailto:${email}`} underline="hover" sx={{ display: 'block' }}>
                                                {email}
                                            </Link>
                                        ))
                                    }
                                />
                                <CardContent>
                                    <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                                        <PhoneIcon fontSize="small" color="action" />
                                        <Typography variant="body1">{org.phone}</Typography>
                                    </Stack>
                                    <Stack direction="row" alignItems="flex-start" spacing={1}>
                                        <LocationOnIcon fontSize="small" color="action" />
                                        <Typography variant="body2">
                                            {org.address}<br />
                                            {org.city}, {org.state} {org.zip}
                                        </Typography>
                                    </Stack>
                                    {pricingData[org.id] && (
                                        <Button size="small" sx={{ mt: 1 }} onClick={() => togglePricing(org)}
                                            endIcon={expandedOrg?.id === org.id ? <ExpandLessIcon /> : <ExpandMoreIcon />}>
                                            Pricing
                                        </Button>
                                    )}
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
                <Collapse in={!!expanded}>
                    {expanded && (
                        <Card sx={{ mt: 3, p: 2 }}>
                            <Typography variant="h5" sx={{ mb: 1 }}>Pricing for {expandedOrg.name}</Typography>
                            <Grid container spacing={3}>
                                {expanded.priorities.filter(p => p.weekdayPrice || p.weekendPrice).length > 0 && (
                                    <Grid item xs={12} sm={3}>
                                        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>Priorities</Typography>
                                        <Stack spacing={1}>
                                            {expanded.priorities.filter(p => p.weekdayPrice || p.weekendPrice).map(p => (
                                                <Card key={p.id} variant="outlined" sx={{ px: 1.5, py: 0.5 }}>
                                                    <Typography variant="body2">{p.name}</Typography>
                                                    {p.weekdayPrice ? <Typography variant="caption" color="text.secondary">Weekday: ${p.weekdayPrice.toFixed(2)}</Typography> : null}
                                                    {p.weekendPrice ? <Typography variant="caption" color="text.secondary" sx={{ ml: p.weekdayPrice ? 1 : 0 }}>Weekend: ${p.weekendPrice.toFixed(2)}</Typography> : null}
                                                </Card>
                                            ))}
                                        </Stack>
                                    </Grid>
                                )}

                                {expanded.billing.filter(b => b.price).length > 0 && (
                                    <Grid item xs={12} sm={expanded.priorities.filter(p => p.weekdayPrice || p.weekendPrice).length > 0 ? 9 : 12}>
                                        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>Billing Codes</Typography>
                                        <TextField size="small" placeholder="Filter by name" value={billingFilter} onChange={e => setBillingFilter(e.target.value)} sx={{ mb: 1, maxWidth: 250 }} />
                                        <Grid container spacing={2}>
                                            {expanded.billing.filter(b => b.price && b.name.toLowerCase().includes(billingFilter.toLowerCase())).map(item => (
                                                <Grid item xs={6} sm={4} md={3} key={item.id}>
                                                    <Card variant="outlined" sx={{ px: 1.5, py: 0.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                        <Typography variant="body2">{item.name}</Typography>
                                                        <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>${item.price.toFixed(2)}</Typography>
                                                    </Card>
                                                </Grid>
                                            ))}
                                        </Grid>
                                    </Grid>
                                )}
                            </Grid>
                        </Card>
                    )}
                </Collapse>
            </>)}
        </>
    )
}

export default Home;
